import type * as React from 'react';
import { del } from '@Domain/Api';
import subjectMemberRemoved, {
	type SubjectMemberRemovedAction,
} from '@Application/Subjects/Members/Actions/subjectMemberRemoved';
import subjectMemberRemovalFailed, {
	type SubjectMemberRemovalFailedAction,
} from '@Application/Subjects/Members/Actions/subjectMemberRemovalFailed';
import type RemoveSubjectMember from '@Application/Subjects/Members/Commands/RemoveSubjectMember';
import buildSubjectApiPath from '@Application/Subjects/buildSubjectApiPath';
import fetchSubjectMembers from '@Application/Subjects/Members/fetchSubjectMembers';
import fetchSubjectUsers from '@Application/Subjects/Members/fetchSubjectUsers';
import FetchSubjectMembers from '@Application/Subjects/Members/Queries/FetchSubjectMembers';
import FetchSubjectUsers from '@Application/Subjects/Members/Queries/FetchSubjectUsers';
import type { SubjectMembersFetchedAction } from '@Application/Subjects/Members/Actions/subjectMembersFetched';
import type { SubjectMembersFetchFailedAction } from '@Application/Subjects/Members/Actions/subjectMembersFetchFailed';
import type { SubjectUsersFetchedAction } from '@Application/Subjects/Members/Actions/subjectUsersFetched';
import type { SubjectUsersFetchFailedAction } from '@Application/Subjects/Members/Actions/subjectUsersFetchFailed';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';

type Actions =
	| SubjectMemberRemovedAction
	| SubjectMemberRemovalFailedAction
	| SubjectMembersFetchedAction
	| SubjectMembersFetchFailedAction
	| SubjectUsersFetchedAction
	| SubjectUsersFetchFailedAction;

/** `204` carries no list, so members and candidates are refetched; a failed refetch is ignored. */
export default (command: RemoveSubjectMember) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			await del<void>(
				`${buildSubjectApiPath(command.facilityId, command.subject)}/members/${command.userId}`,
			);

			const action = subjectMemberRemoved(command.subject.subjectId, command.userId);
			dispatch(action);
			await fetchSubjectMembers(new FetchSubjectMembers(command.facilityId, command.subject))(
				dispatch,
			).catch(() => undefined);
			await fetchSubjectUsers(new FetchSubjectUsers(command.facilityId, command.subject))(
				dispatch,
			).catch(() => undefined);

			return action;
		} catch (error) {
			const action = subjectMemberRemovalFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
